// src/components/StundenAuszug.jsx
import React, { useState, useEffect, useMemo } from "react";
import * as XLSX from "xlsx";
import { getTimekeepingData } from "../services/azureTimekeepingService";
import { getProjectAssignments } from "../services/azureProjectAssignmentService";
import { getProducts } from "../services/apiService";
import { getProjectPhases } from "../services/azureProjectPhasesService";
import { getAllTeamMembers } from "../services/azureTeamService";
import {
  getInvoicedPeriods,
  saveInvoicedPeriod,
  deleteInvoicedPeriod,
} from "../services/azureProjectInvoicedPeriodsService";
import { getProjectRates } from "../services/azureRatesService";
import {
  getBudgetEntries,
  saveBudgetEntry,
  calculateDueDate,
} from "../services/azureBudgetService";
import {
  calculateHours,
  getHourlyRate,
  getInternalRate,
  calculateAmount,
  isEntryInvoiced,
  formatCHF,
  formatDateCH,
  formatDateTimeCH,
  sortByDate,
  groupByPerson,
  calculateSummary,
  filterByDateRange,
  findOverlappingPeriods,
} from "../utils/stundenAuszugUtils";

const StundenAuszug = ({ project, projectCode, employees, readOnly = false, allProjects = [], onProjectChange }) => {
  // Data states
  const [timekeepingEntries, setTimekeepingEntries] = useState([]);
  const [projectAssignments, setProjectAssignments] = useState([]);
  const [products, setProducts] = useState([]);
  const [projectPhases, setProjectPhases] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [invoicedPeriods, setInvoicedPeriods] = useState([]);
  const [projectRates, setProjectRates] = useState([]);

  // UI states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortMode, setSortMode] = useState("date"); // "date" | "person"
  const [rateMode, setRateMode] = useState("extern"); // "extern" | "intern"
  const [showInvoiced, setShowInvoiced] = useState(false);

  // Modal states
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showMarkInvoicedDialog, setShowMarkInvoicedDialog] = useState(false);
  const [exportDateFrom, setExportDateFrom] = useState("");
  const [exportDateTo, setExportDateTo] = useState("");
  const [markInvoicedDateFrom, setMarkInvoicedDateFrom] = useState("");
  const [markInvoicedDateTo, setMarkInvoicedDateTo] = useState("");
  const [invoiceReference, setInvoiceReference] = useState("");

  // Budget linking states
  const [budgetSuggestions, setBudgetSuggestions] = useState([]);
  const [budgetSuggestionsLoading, setBudgetSuggestionsLoading] = useState(false);
  const [selectedBudgetEntry, setSelectedBudgetEntry] = useState(null);
  const [editableBruttoAmount, setEditableBruttoAmount] = useState(null);

  // Load data on mount
  useEffect(() => {
    loadAllData();
  }, [projectCode]);

  const loadAllData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load all data in parallel
      const [
        timekeeping,
        assignments,
        productsData,
        phases,
        team,
        invoiced,
        ratesData,
      ] = await Promise.all([
        getTimekeepingData(),
        getProjectAssignments(projectCode),
        getProducts(),
        getProjectPhases(projectCode),
        getAllTeamMembers(),
        getInvoicedPeriods(projectCode),
        getProjectRates(projectCode),
      ]);

      // Filter timekeeping by project code (first 3 chars match)
      const projectTimekeeping = timekeeping.filter(
        (entry) => entry.code === projectCode
      );

      setTimekeepingEntries(projectTimekeeping);
      setProjectAssignments(assignments);
      setProducts(productsData);
      setProjectPhases(phases);
      setTeamMembers(team);
      setInvoicedPeriods(invoiced);
      setProjectRates(ratesData);
    } catch (err) {
      console.error("Error loading data:", err);
      setError("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  // Filter entries based on showInvoiced toggle
  const displayedEntries = useMemo(() => {
    if (showInvoiced) {
      return timekeepingEntries;
    }
    return timekeepingEntries.filter(
      (entry) => !isEntryInvoiced(entry, invoicedPeriods)
    );
  }, [timekeepingEntries, invoicedPeriods, showInvoiced]);

  // Sort or group entries
  const sortedEntries = useMemo(() => {
    if (sortMode === "date") {
      return sortByDate(displayedEntries);
    }
    // For "person" mode, we'll use groupByPerson in rendering
    return displayedEntries;
  }, [displayedEntries, sortMode]);

  // Resolve rate for an entry depending on the selected rate mode
  const resolveRate = (entry) =>
    rateMode === "intern"
      ? getInternalRate(entry, teamMembers)
      : getHourlyRate(
          entry,
          projectPhases,
          projectAssignments,
          products,
          teamMembers,
          projectRates
        );

  // Calculate summary
  const summary = useMemo(() => {
    return calculateSummary(
      timekeepingEntries,
      projectPhases,
      projectAssignments,
      products,
      teamMembers,
      invoicedPeriods,
      projectRates,
      rateMode
    );
  }, [
    timekeepingEntries,
    projectPhases,
    projectAssignments,
    products,
    teamMembers,
    invoicedPeriods,
    projectRates,
    rateMode,
  ]);

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  // Get oldest non-invoiced entry date
  const getOldestNonInvoicedDate = () => {
    const nonInvoiced = timekeepingEntries.filter(
      (entry) => !isEntryInvoiced(entry, invoicedPeriods)
    );

    if (nonInvoiced.length === 0) return "";

    const dates = nonInvoiced.map((entry) => entry.date).filter((d) => d);
    if (dates.length === 0) return "";

    return dates.sort()[0]; // Oldest date
  };

  // Handle Excel export
  const handleExport = () => {
    if (!exportDateFrom || !exportDateTo) {
      alert("Bitte Von- und Bis-Datum auswählen");
      return;
    }

    if (exportDateFrom > exportDateTo) {
      alert("Von-Datum muss vor Bis-Datum liegen");
      return;
    }

    const filteredEntries = filterByDateRange(
      timekeepingEntries,
      exportDateFrom,
      exportDateTo
    ).filter((entry) => !isEntryInvoiced(entry, invoicedPeriods));

    if (filteredEntries.length === 0) {
      alert("Keine Einträge im gewählten Zeitraum gefunden");
      return;
    }

    const rateColumnLabel =
      rateMode === "intern" ? "Stundensatz (intern)" : "Stundensatz (extern)";

    const excelData = filteredEntries.map((entry) => {
      const rate = resolveRate(entry);
      const hours = calculateHours(entry);
      const amount = calculateAmount(entry, rate);

      return {
        Datum: formatDateCH(entry.date),
        "Uhrzeit Von": entry.startTime || "",
        "Uhrzeit Bis": entry.endTime || "",
        "Anzahl Stunden": hours.toFixed(2),
        "Name Person": entry.employeeName,
        [rateColumnLabel]: rate.toFixed(2),
        "Betrag CHF": amount.toFixed(2),
        Notiz: entry.comment || "",
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    worksheet["!cols"] = [
      { wch: 12 }, // Datum
      { wch: 10 }, // Von
      { wch: 10 }, // Bis
      { wch: 12 }, // Stunden
      { wch: 20 }, // Person
      { wch: 15 }, // Stundensatz
      { wch: 12 }, // Betrag
      { wch: 40 }, // Notiz
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Stundenauszug");

    const fileName = `${projectCode}_${project.title || "Projekt"}_Stundenauszug_${rateMode}_${exportDateFrom}_${exportDateTo}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    setShowExportDialog(false);
    setExportDateFrom("");
    setExportDateTo("");
  };

  // Handle mark as invoiced
  const handleMarkInvoiced = async () => {
    if (!markInvoicedDateFrom || !markInvoicedDateTo) {
      alert("Bitte Von- und Bis-Datum auswählen");
      return;
    }

    if (markInvoicedDateFrom > markInvoicedDateTo) {
      alert("Von-Datum muss vor Bis-Datum liegen");
      return;
    }

    // Check for overlapping periods
    const overlapping = findOverlappingPeriods(
      markInvoicedDateFrom,
      markInvoicedDateTo,
      invoicedPeriods
    );

    if (overlapping.length > 0) {
      const overlapMsg = overlapping
        .map(
          (p) =>
            `${formatDateCH(p.dateFrom)} - ${formatDateCH(p.dateTo)}`
        )
        .join("\n");

      if (
        !window.confirm(
          `Es existiert bereits eine Faktura für Teile dieses Zeitraums:\n\n${overlapMsg}\n\nTrotzdem fortfahren?`
        )
      ) {
        return;
      }
    }

    // Calculate totals for the period
    const entriesInRange = filterByDateRange(
      timekeepingEntries,
      markInvoicedDateFrom,
      markInvoicedDateTo
    ).filter((entry) => !isEntryInvoiced(entry, invoicedPeriods));

    if (entriesInRange.length === 0) {
      alert("Keine nicht-versendeten Einträge im gewählten Zeitraum gefunden");
      return;
    }

    const totalHours = entriesInRange.reduce((sum, entry) => {
      return sum + calculateHours(entry);
    }, 0);

    const totalCHF = entriesInRange.reduce((sum, entry) => {
      const rate = getHourlyRate(
        entry,
        projectPhases,
        projectAssignments,
        products,
        teamMembers,
        projectRates
      );
      return sum + calculateAmount(entry, rate);
    }, 0);

    try {
      setLoading(true);

      const periodData = {
        dateFrom: markInvoicedDateFrom,
        dateTo: markInvoicedDateTo,
        totalHours,
        totalCHF,
        invoiceReference: invoiceReference || "",
        createdBy: "",
        linkedBudgetEntryId: selectedBudgetEntry ? selectedBudgetEntry.id : "",
      };

      const saveResult = await saveInvoicedPeriod(projectCode, periodData);

      // Update linked budget entry if selected
      if (selectedBudgetEntry) {
        const today = new Date().toISOString().split("T")[0];
        const finalBrutto = editableBruttoAmount || totalCHF * 1.081;

        await saveBudgetEntry({
          ...selectedBudgetEntry,
          betrag: finalBrutto,
          ist_brutto: true,
          status: "sent",
          versand_datum: today,
          sent_date: today,
          invoice_reference: invoiceReference || "",
          linked_invoiced_period_id: saveResult.rowKey,
        });
      }

      // Reload invoiced periods
      const updatedPeriods = await getInvoicedPeriods(projectCode);
      setInvoicedPeriods(updatedPeriods);

      // Close dialog and reset fields
      setShowMarkInvoicedDialog(false);
      setMarkInvoicedDateFrom("");
      setMarkInvoicedDateTo("");
      setInvoiceReference("");
      setSelectedBudgetEntry(null);
      setEditableBruttoAmount(null);
      setBudgetSuggestions([]);

      alert("Zeitraum erfolgreich als versendet markiert");
    } catch (err) {
      console.error("Error marking as invoiced:", err);
      alert("Fehler beim Markieren als versendet");
    } finally {
      setLoading(false);
    }
  };

  // Handle delete invoiced period
  const handleDeleteInvoicedPeriod = async (period) => {
    if (
      !window.confirm(
        `Faktura-Zeitraum löschen?\n\n${formatDateCH(period.dateFrom)} - ${formatDateCH(period.dateTo)}\n${period.totalHours.toFixed(2)}h, ${formatCHF(period.totalCHF)}\n\nDiese Aktion kann nicht rückgängig gemacht werden.`
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      await deleteInvoicedPeriod(projectCode, period.rowKey);

      // Reload invoiced periods
      const updatedPeriods = await getInvoicedPeriods(projectCode);
      setInvoicedPeriods(updatedPeriods);

      alert("Faktura-Zeitraum erfolgreich gelöscht");
    } catch (err) {
      console.error("Error deleting invoiced period:", err);
      alert("Fehler beim Löschen des Faktura-Zeitraums");
    } finally {
      setLoading(false);
    }
  };

  // Preview totals for mark as invoiced dialog
  const markInvoicedPreview = useMemo(() => {
    if (!markInvoicedDateFrom || !markInvoicedDateTo) {
      return { hours: 0, amount: 0, amountIntern: 0, count: 0 };
    }

    const entriesInRange = filterByDateRange(
      timekeepingEntries,
      markInvoicedDateFrom,
      markInvoicedDateTo
    ).filter((entry) => !isEntryInvoiced(entry, invoicedPeriods));

    const totalHours = entriesInRange.reduce(
      (sum, entry) => sum + calculateHours(entry),
      0
    );

    const totalAmount = entriesInRange.reduce((sum, entry) => {
      const rate = getHourlyRate(
        entry,
        projectPhases,
        projectAssignments,
        products,
        teamMembers,
        projectRates
      );
      return sum + calculateAmount(entry, rate);
    }, 0);

    // Calculate internal amount (team member internal rates)
    const totalAmountIntern = entriesInRange.reduce((sum, entry) => {
      const personId = entry.employeeUuid;
      const tm = teamMembers.find((m) => m.employeeUuid === personId);
      const internalRate = tm?.internalRate || 0;
      return sum + calculateAmount(entry, internalRate);
    }, 0);

    return {
      hours: totalHours,
      amount: totalAmount,
      amountIntern: totalAmountIntern,
      count: entriesInRange.length,
    };
  }, [
    markInvoicedDateFrom,
    markInvoicedDateTo,
    timekeepingEntries,
    invoicedPeriods,
    projectPhases,
    projectAssignments,
    products,
    teamMembers,
  ]);

  // Detect which project phase overlaps the Von/Bis date range
  const detectPhaseForDateRange = (dateFrom, dateTo) => {
    if (!dateFrom || !dateTo || !projectPhases || projectPhases.length === 0) {
      return null;
    }

    const overlapping = projectPhases.filter((phase) => {
      if (!phase.startDate || !phase.endDate) return false;
      return phase.startDate <= dateTo && phase.endDate >= dateFrom;
    });

    if (overlapping.length === 0) return null;
    if (overlapping.length === 1) return overlapping[0];

    // Multiple phases overlap - pick the one with greatest overlap
    const withOverlap = overlapping.map((phase) => {
      const overlapStart = phase.startDate > dateFrom ? phase.startDate : dateFrom;
      const overlapEnd = phase.endDate < dateTo ? phase.endDate : dateTo;
      const overlapDays =
        (new Date(overlapEnd) - new Date(overlapStart)) / (1000 * 60 * 60 * 24);
      return { ...phase, overlapDays };
    });
    withOverlap.sort((a, b) => b.overlapDays - a.overlapDays);
    return withOverlap[0];
  };

  // Load budget suggestions when mark-invoiced dialog opens
  useEffect(() => {
    if (showMarkInvoicedDialog && markInvoicedDateFrom && markInvoicedDateTo) {
      loadBudgetSuggestions();
    } else if (!showMarkInvoicedDialog) {
      setBudgetSuggestions([]);
      setSelectedBudgetEntry(null);
      setEditableBruttoAmount(null);
    }
  }, [showMarkInvoicedDialog, markInvoicedDateFrom, markInvoicedDateTo]);

  const loadBudgetSuggestions = async () => {
    setBudgetSuggestionsLoading(true);
    try {
      const allEntries = await getBudgetEntries();

      // Filter: Einnahme, Einmalig, not paid
      const candidates = allEntries.filter((entry) => {
        if (entry.typ !== "Einnahme") return false;
        if (entry.typ_timing !== "Einmalig") return false;
        if (entry.status === "paid" || entry.paid_date) return false;
        return true;
      });

      const detectedPhase = detectPhaseForDateRange(
        markInvoicedDateFrom,
        markInvoicedDateTo
      );

      const stundenBrutto = markInvoicedPreview.amount * 1.081;

      const scored = candidates.map((entry) => {
        let score = 0;

        if (entry.projekt === projectCode) score += 100;
        else if (entry.name && entry.name.includes(projectCode)) score += 50;

        if (detectedPhase && entry.phase && entry.phase === detectedPhase.name) {
          score += 40;
        }

        const budgetBrutto = entry.brutto_betrag || entry.betrag || 0;
        const diffPenalty = Math.min(
          Math.abs(budgetBrutto - stundenBrutto) / 1000,
          30
        );
        score -= diffPenalty;

        return { ...entry, matchScore: score };
      });

      const relevant = scored
        .filter((s) => s.matchScore > 0)
        .sort((a, b) => b.matchScore - a.matchScore);

      setBudgetSuggestions(relevant);
    } catch (error) {
      console.error("Error loading budget suggestions:", error);
      setBudgetSuggestions([]);
    } finally {
      setBudgetSuggestionsLoading(false);
    }
  };

  const handleCreateNewBudgetEntry = async () => {
    const detectedPhase = detectPhaseForDateRange(
      markInvoicedDateFrom,
      markInvoicedDateTo
    );
    const auszugBrutto = markInvoicedPreview.amount * 1.081;
    const today = new Date().toISOString().split("T")[0];

    const newEntry = {
      typ: "Einnahme",
      typ_timing: "Einmalig",
      name: `${projectCode} - Stundenauszug ${formatDateCH(markInvoicedDateFrom)} - ${formatDateCH(markInvoicedDateTo)}`,
      projekt: projectCode,
      phase: detectedPhase ? detectedPhase.name : "",
      betrag: auszugBrutto,
      mwst_satz: 8.1,
      ist_brutto: true,
      konto: "3400",
      versand_datum: today,
      sent_date: today,
      zahlungsfrist: 30,
      status: "sent",
    };

    try {
      setBudgetSuggestionsLoading(true);
      const savedEntry = await saveBudgetEntry(newEntry);

      const fullEntry = {
        ...newEntry,
        id: savedEntry.id,
        brutto_betrag: savedEntry.bruttoBetrag || auszugBrutto,
        netto_betrag: savedEntry.nettoBetrag || auszugBrutto / 1.081,
        mwst_betrag: savedEntry.mwstBetrag || auszugBrutto - auszugBrutto / 1.081,
      };

      setBudgetSuggestions((prev) => [fullEntry, ...prev]);
      setSelectedBudgetEntry(fullEntry);
      setEditableBruttoAmount(fullEntry.brutto_betrag);
    } catch (error) {
      console.error("Error creating budget entry:", error);
      alert("Fehler beim Erstellen der Budget-Position");
    } finally {
      setBudgetSuggestionsLoading(false);
    }
  };

  // Render loading state
  if (loading && timekeepingEntries.length === 0) {
    return (
      <div className="stunden-auszug">
        <div className="stunden-auszug-loading">
          <p>Lade Stundendaten...</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="stunden-auszug">
        <div className="stunden-auszug-error">
          <p>{error}</p>
          <button className="btn btn-primary" onClick={loadAllData}>
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  // Render main component
  return (
    <div className="stunden-auszug">
      {/* Header */}
      <div className="stunden-auszug-header">
        <div>
          <h2>
            Stundenauszug:
            {allProjects.length > 0 && onProjectChange ? (
              <select
                value={projectCode}
                onChange={(e) => {
                  const newProject = allProjects.find((p) => p.code === e.target.value);
                  if (newProject) onProjectChange(newProject);
                }}
                className="project-selector"
                style={{ marginLeft: '10px' }}
              >
                {allProjects
                  .filter((p) => p.type === "project" || !p.type)
                  .sort((a, b) => b.code.localeCompare(a.code))
                  .map((p) => (
                    <option key={p.code} value={p.code}>{p.code} - {p.title}</option>
                  ))}
              </select>
            ) : (
              <> {projectCode} {project.title || ""}</>
            )}
          </h2>
          <p className="text-muted">
            {timekeepingEntries.length} Zeiteinträge gefunden
          </p>
        </div>
      </div>

      {/* No data message */}
      {timekeepingEntries.length === 0 && (
        <div className="stunden-auszug-empty">
          <p>Keine Zeiteinträge für dieses Projekt erfasst.</p>
        </div>
      )}

      {/* Controls */}
      {timekeepingEntries.length > 0 && (
        <>
          <div className="stunden-auszug-controls">
            {/* Rate mode toggle */}
            <div className="sort-toggle rate-toggle">
              <label>Stundenansatz:</label>
              <button
                className={`btn btn-sm ${rateMode === "extern" ? "btn-primary" : "btn-secondary"}`}
                onClick={() => setRateMode("extern")}
              >
                Extern
              </button>
              <button
                className={`btn btn-sm ${rateMode === "intern" ? "btn-primary" : "btn-secondary"}`}
                onClick={() => setRateMode("intern")}
              >
                Intern
              </button>
            </div>

            {/* Sort mode toggle */}
            <div className="sort-toggle">
              <label>Sortierung:</label>
              <button
                className={`btn btn-sm ${sortMode === "date" ? "btn-primary" : "btn-secondary"}`}
                onClick={() => setSortMode("date")}
              >
                Nach Datum
              </button>
              <button
                className={`btn btn-sm ${sortMode === "person" ? "btn-primary" : "btn-secondary"}`}
                onClick={() => setSortMode("person")}
              >
                Nach Person
              </button>
            </div>

            {/* Show invoiced toggle */}
            <div className="filter-checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={showInvoiced}
                  onChange={(e) => setShowInvoiced(e.target.checked)}
                />
                Versendete anzeigen
              </label>
            </div>

            {/* Action buttons */}
            <div className="action-buttons">
              <button
                className="btn btn-primary btn-sm"
                onClick={() => {
                  setExportDateFrom(getOldestNonInvoicedDate() || "");
                  setExportDateTo(getTodayDate());
                  setShowExportDialog(true);
                }}
                disabled={timekeepingEntries.length === 0}
              >
                Excel Export
              </button>
              <button
                className="btn btn-success btn-sm"
                onClick={() => {
                  setMarkInvoicedDateFrom(getOldestNonInvoicedDate() || "");
                  setMarkInvoicedDateTo(getTodayDate());
                  setShowMarkInvoicedDialog(true);
                }}
                disabled={readOnly || timekeepingEntries.length === 0}
              >
                Als versendet markieren
              </button>
            </div>
          </div>

          {/* Summary section */}
          <div className="stunden-auszug-summary">
            <div className="summary-row">
              <span>
                Zwischensumme {rateMode === "intern" ? "intern" : "extern"}{" "}
                (nicht versendet):
              </span>
              <strong>{formatCHF(summary.zwischensumme)}</strong>
            </div>
            {rateMode === "extern" && (
              <>
                <div className="summary-row">
                  <span>MwSt 8.1%:</span>
                  <strong>{formatCHF(summary.mwst)}</strong>
                </div>
                <div className="summary-row summary-total">
                  <span>Total (inkl. MwSt):</span>
                  <strong>{formatCHF(summary.total)}</strong>
                </div>
              </>
            )}
          </div>

          {/* Table */}
          <div className="stunden-auszug-table-container">
            <table className="stunden-auszug-table team-table">
              <thead>
                <tr>
                  <th>Datum</th>
                  <th>Uhrzeit</th>
                  <th>Stunden</th>
                  <th>Person</th>
                  <th>
                    Stundensatz ({rateMode === "intern" ? "intern" : "extern"})
                  </th>
                  <th>Betrag</th>
                  <th>Notiz</th>
                </tr>
              </thead>
              <tbody>
                {sortMode === "date"
                  ? sortedEntries.map((entry, index) => {
                      const rate = resolveRate(entry);
                      const hours = calculateHours(entry);
                      const amount = calculateAmount(entry, rate);
                      const invoiced = isEntryInvoiced(entry, invoicedPeriods);

                      return (
                        <tr
                          key={index}
                          className={invoiced ? "entry-invoiced" : ""}
                        >
                          <td>{formatDateCH(entry.date)}</td>
                          <td>
                            {entry.startTime && entry.endTime
                              ? `${entry.startTime} - ${entry.endTime}`
                              : ""}
                          </td>
                          <td>{hours.toFixed(2)}h</td>
                          <td>{entry.employeeName}</td>
                          <td>
                            {formatCHF(rate)}
                            {rate === 0 && (
                              <span
                                className="warning-icon"
                                title="Kein Stundensatz definiert"
                              >
                                {" "}
                                ⚠️
                              </span>
                            )}
                          </td>
                          <td>{formatCHF(amount)}</td>
                          <td>{entry.comment || ""}</td>
                        </tr>
                      );
                    })
                  : // Group by person mode
                    Object.entries(
                      groupByPerson(
                        sortedEntries,
                        projectPhases,
                        projectAssignments,
                        products,
                        teamMembers,
                        projectRates,
                        rateMode
                      )
                    ).map(([personName, personData]) => (
                      <React.Fragment key={personName}>
                        <tr className="person-group-header">
                          <td colSpan="7">
                            <strong>{personName}</strong>
                          </td>
                        </tr>
                        {personData.entries.map((entry, index) => {
                          const invoiced = isEntryInvoiced(
                            entry,
                            invoicedPeriods
                          );
                          return (
                            <tr
                              key={index}
                              className={invoiced ? "entry-invoiced" : ""}
                            >
                              <td>{formatDateCH(entry.date)}</td>
                              <td>
                                {entry.startTime && entry.endTime
                                  ? `${entry.startTime} - ${entry.endTime}`
                                  : ""}
                              </td>
                              <td>{entry.hours.toFixed(2)}h</td>
                              <td>{entry.employeeName}</td>
                              <td>
                                {formatCHF(entry.rate)}
                                {entry.rate === 0 && (
                                  <span
                                    className="warning-icon"
                                    title="Kein Stundensatz definiert"
                                  >
                                    {" "}
                                    ⚠️
                                  </span>
                                )}
                              </td>
                              <td>{formatCHF(entry.amount)}</td>
                              <td>{entry.comment || ""}</td>
                            </tr>
                          );
                        })}
                        <tr className="person-subtotal">
                          <td colSpan="2">
                            <strong>Zwischensumme {personName}</strong>
                          </td>
                          <td>
                            <strong>{personData.totalHours.toFixed(2)}h</strong>
                          </td>
                          <td colSpan="2"></td>
                          <td>
                            <strong>{formatCHF(personData.totalAmount)}</strong>
                          </td>
                          <td></td>
                        </tr>
                      </React.Fragment>
                    ))}
              </tbody>
            </table>
          </div>

          {/* Invoiced History */}
          {invoicedPeriods.length > 0 && (
            <div className="invoiced-history">
              <h3>Versendete Zeiträume</h3>
              {invoicedPeriods.map((period) => (
                <div className="history-item" key={period.rowKey}>
                  <div className="history-item-info">
                    <strong>
                      {formatDateCH(period.dateFrom)} -{" "}
                      {formatDateCH(period.dateTo)}
                    </strong>
                    <div className="history-item-details">
                      {period.totalHours.toFixed(2)}h •{" "}
                      {formatCHF(period.totalCHF)}
                    </div>
                    {period.invoiceReference && (
                      <div className="history-item-reference">
                        Rechnung: {period.invoiceReference}
                      </div>
                    )}
                    <small className="text-muted">
                      {formatDateTimeCH(period.createdAt)}
                    </small>
                  </div>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleDeleteInvoicedPeriod(period)}
                    disabled={readOnly}
                  >
                    Löschen
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Export Dialog */}
      {showExportDialog && (
        <div className="modal-overlay" onClick={() => setShowExportDialog(false)}>
          <div className="modal-content export-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Excel Export</h3>
            <p>Bitte wählen Sie den Zeitraum für den Export:</p>
            <div className="date-range-inputs">
              <div className="form-group">
                <label>Von:</label>
                <input
                  type="date"
                  value={exportDateFrom}
                  onChange={(e) => setExportDateFrom(e.target.value)}
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label>Bis:</label>
                <div className="date-input-with-button">
                  <input
                    type="date"
                    value={exportDateTo}
                    onChange={(e) => setExportDateTo(e.target.value)}
                    className="form-control"
                  />
                  <button
                    className="btn btn-sm btn-secondary today-btn"
                    onClick={() => setExportDateTo(getTodayDate())}
                    type="button"
                  >
                    Heute
                  </button>
                </div>
              </div>
            </div>
            <p className="text-muted">
              Hinweis: Es werden nur nicht-versendete Stunden exportiert. Der
              Export erfolgt mit den{" "}
              <strong>
                {rateMode === "intern" ? "internen" : "externen"}
              </strong>{" "}
              Stundenansätzen (gemäss Auswahl oben).
            </p>
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowExportDialog(false)}
              >
                Abbrechen
              </button>
              <button className="btn btn-primary" onClick={handleExport}>
                Exportieren
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mark as Invoiced Dialog */}
      {showMarkInvoicedDialog && (
        <div
          className="modal-overlay"
          onClick={() => setShowMarkInvoicedDialog(false)}
        >
          <div
            className="modal-content mark-invoiced-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Als versendet markieren</h3>
            <p>Bitte wählen Sie den Zeitraum:</p>
            <div className="date-range-inputs">
              <div className="form-group">
                <label>Von:</label>
                <input
                  type="date"
                  value={markInvoicedDateFrom}
                  onChange={(e) => setMarkInvoicedDateFrom(e.target.value)}
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label>Bis:</label>
                <div className="date-input-with-button">
                  <input
                    type="date"
                    value={markInvoicedDateTo}
                    onChange={(e) => setMarkInvoicedDateTo(e.target.value)}
                    className="form-control"
                  />
                  <button
                    className="btn btn-sm btn-secondary today-btn"
                    onClick={() => setMarkInvoicedDateTo(getTodayDate())}
                    type="button"
                  >
                    Heute
                  </button>
                </div>
              </div>
            </div>

            {markInvoicedPreview.count > 0 && (
              <div className="preview-summary">
                <strong>Vorschau:</strong>
                <div>{markInvoicedPreview.count} Einträge</div>
                <div>{markInvoicedPreview.hours.toFixed(2)} Stunden</div>
                <div className="preview-amounts">
                  <div className="preview-amount-col">
                    <label>Extern</label>
                    <span className="amount-value">
                      {formatCHF(markInvoicedPreview.amount)}
                    </span>
                  </div>
                  <div className="preview-amount-col">
                    <label>Intern</label>
                    <span className="amount-value">
                      {formatCHF(markInvoicedPreview.amountIntern)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Budget Linking Section */}
            {markInvoicedPreview.count > 0 && (
              <div className="budget-linking-section">
                <h4>
                  Budget-Verknüpfung{" "}
                  <span className="optional-badge">optional</span>
                </h4>

                {budgetSuggestionsLoading && (
                  <div className="budget-loading-inline">
                    Lade Budget-Vorschläge...
                  </div>
                )}

                {!budgetSuggestionsLoading && budgetSuggestions.length > 0 && (
                  <div className="budget-suggestions-list">
                    {budgetSuggestions.map((suggestion) => (
                      <div
                        key={suggestion.id}
                        className={`budget-suggestion-item ${
                          selectedBudgetEntry?.id === suggestion.id
                            ? "selected"
                            : ""
                        }`}
                        onClick={() => {
                          if (selectedBudgetEntry?.id === suggestion.id) {
                            setSelectedBudgetEntry(null);
                            setEditableBruttoAmount(null);
                          } else {
                            setSelectedBudgetEntry(suggestion);
                            setEditableBruttoAmount(
                              suggestion.brutto_betrag || suggestion.betrag
                            );
                          }
                        }}
                      >
                        <div className="suggestion-info">
                          <div className="suggestion-name">
                            {suggestion.name}
                          </div>
                          <div className="suggestion-meta">
                            <span className="suggestion-amount">
                              {formatCHF(
                                suggestion.brutto_betrag || suggestion.betrag
                              )}{" "}
                              (brutto)
                            </span>
                            {suggestion.phase && (
                              <span className="suggestion-phase">
                                Phase: {suggestion.phase}
                              </span>
                            )}
                            {suggestion.wann && (
                              <span className="suggestion-due">
                                Fällig: {formatDateCH((calculateDueDate(suggestion) || new Date(suggestion.wann)).toISOString().split("T")[0])}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="suggestion-check">
                          {selectedBudgetEntry?.id === suggestion.id
                            ? "\u2713"
                            : ""}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {!budgetSuggestionsLoading &&
                  budgetSuggestions.length === 0 && (
                    <div className="no-budget-suggestions">
                      Keine passenden Budget-Positionen gefunden.
                    </div>
                  )}

                <button
                  type="button"
                  className="btn btn-sm btn-secondary create-budget-btn"
                  onClick={handleCreateNewBudgetEntry}
                  disabled={budgetSuggestionsLoading}
                >
                  + Neue Budget-Position erstellen
                </button>

                {selectedBudgetEntry && (
                  <div className="budget-comparison">
                    <div className="comparison-row">
                      <div className="comparison-col">
                        <label>Budget-Betrag (brutto)</label>
                        <div className="comparison-value budget-value">
                          {formatCHF(
                            selectedBudgetEntry.brutto_betrag ||
                              selectedBudgetEntry.betrag
                          )}
                        </div>
                      </div>
                      <div className="comparison-col">
                        <label>Stundenauszug (brutto)</label>
                        <div className="comparison-value auszug-value">
                          {formatCHF(markInvoicedPreview.amount * 1.081)}
                        </div>
                      </div>
                    </div>

                    <div className="form-group editable-amount">
                      <label>Rechnungsbetrag (brutto, editierbar):</label>
                      <input
                        type="number"
                        step="0.01"
                        value={editableBruttoAmount || ""}
                        onChange={(e) =>
                          setEditableBruttoAmount(
                            parseFloat(e.target.value) || 0
                          )
                        }
                        className="form-control"
                      />
                    </div>

                    {(() => {
                      const budgetBrutto =
                        selectedBudgetEntry.brutto_betrag ||
                        selectedBudgetEntry.betrag ||
                        0;
                      const deviation =
                        budgetBrutto > 0
                          ? Math.abs(editableBruttoAmount - budgetBrutto) /
                            budgetBrutto
                          : 0;
                      if (deviation > 0.15) {
                        return (
                          <div className="budget-deviation-warning">
                            Abweichung von {(deviation * 100).toFixed(1)}% zum
                            Budget-Betrag.
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                )}
              </div>
            )}

            <div className="form-group">
              <label>Rechnungsnummer (optional):</label>
              <input
                type="text"
                value={invoiceReference}
                onChange={(e) => setInvoiceReference(e.target.value)}
                className="form-control"
                placeholder="z.B. RE-2026-001"
              />
            </div>

            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowMarkInvoicedDialog(false)}
              >
                Abbrechen
              </button>
              <button
                className="btn btn-success"
                onClick={handleMarkInvoiced}
                disabled={!markInvoicedDateFrom || !markInvoicedDateTo}
              >
                Markieren
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StundenAuszug;
